import { immer } from "zustand/middleware/immer";
import { persist } from "zustand/middleware";
import { create } from "zustand";
import { createSelectors } from "@/lib/storeUtils";
import { getRootApiUrl } from "@/lib/apiConfig";
import { LoginFailedError } from "@/exceptions/LoginFailedError";
import { BRIDGE_EVENTS, bridge } from "@/lib/webphone-bridge/crossFrameworkBridge";
import { isPbxApiInviteToken } from "@/queries/useInvitationQuery";
import { getPbxClient, getSipClient } from "@/lib/apiClient";
import {
    AuthApiError,
    type PasswordResetOutcome,
    loginWithCredentials,
    logoutSession,
    redeemPasswordReset,
    requestPasswordReset,
} from "@/lib/authSession";
import type { SessionEndReason } from "@/lib/sessionChannel";
import { translate } from "@/lib/i18n-config";
import { mobileDeviceModalStore } from "./mobileDeviceModalStore";
import {
    resetAllLoginState,
    setForgotPasswordLoading,
    setLoginLoading,
    setRegisterLoading,
    setResetPasswordLoading,
} from "./loginFlowStore";
import { useInboxStore } from "./inbox-store";

// Re-export auth utilities for backwards compatibility
export { getReturnPath } from "./authUtils";
import { clearSessionCookie, redirectToLogin } from "./authUtils";
import { clearAccountStorage } from "./clearAccountStorage";

export type RoleObject = {
    id: number;
    name: string;
    sip_role_id: number;
    is_for_agent: boolean;
};

export type AuthState = {
    // Core auth — the sipio-id access token, memory only (never persisted; the durable
    // credential is the __Host-pbx_rt HttpOnly cookie on the pbx-api origin)
    authToken: string | null;
    isLoggedIn: boolean;
    isLoggingOut: boolean;

    // Silent-refresh boot gate: "pending" until the boot refresh resolved either way.
    // RootRedirect / DashboardLayoutWrapper hold protected paint on this.
    bootStatus: "pending" | "ready";

    // Tenant routing metadata (non-secret)
    sipSubdomain: string | null;

    // Other
    isUnauthorized: boolean;
    preventSocketActivePlanRefetch: boolean;
};

const baseAuthStore = immer<AuthState>(() => ({
    // Core auth
    authToken: null,
    isLoggedIn: false,
    isLoggingOut: false,
    bootStatus: "pending",

    // Tenant routing metadata
    sipSubdomain: null,

    // Other
    isUnauthorized: false,
    preventSocketActivePlanRefetch: false,
}));

export const authStore = create(
    persist(baseAuthStore, {
        name: "authStore",
        // v1 (CT-36): authToken/sipAppKey no longer persisted — token is memory-only.
        // v2 (CT-136): sipWebDomain (legacy impersonation routing) dropped — never written on v2.
        version: 2,
        partialize: (state) => ({
            sipSubdomain: state.sipSubdomain,
        }),
        migrate: (persistedState) => {
            // Strip credentials persisted by v0 (pre-CT-36) so they never rehydrate.
            const {
                authToken: _token,
                sipAppKey: _appKey,
                sipWebDomain: _sipWebDomain,
                ...rest
            } = (persistedState ?? {}) as Record<string, unknown>;
            // Only the partialized routing fields survive; zustand shallow-merges them
            // over the in-memory initial state.
            return rest as unknown as AuthState;
        },
    })
);

export type LoginData = {
    email: string;
    password?: string | undefined;
    /** The login form's OTP field (LoginForm names it `code`). */
    code?: string;
    one_time_password?: string;
    send_me_email?: boolean;
    confirm_token?: string;
    social_token?: string;
    provider?: string;
};

/** The names the login form speaks, which are sip.io's — sip-idp says totp/email/sms. */
const legacyChannelName = (channel: string): string =>
    channel === "totp" ? "GA" : channel === "email" ? "Email" : "SMS";

/**
 * Map an /api/auth error onto the legacy LoginFailedError shapes LoginForm already renders,
 * so CT-36 swaps the transport without rewriting the form (form cleanup = CT-135).
 */
function mapAuthErrorToLoginFailed(error: unknown): Error {
    if (!(error instanceof AuthApiError)) {
        return error instanceof Error ? error : new Error(String(error));
    }

    if (error.code === "mfa_required" && error.mfa) {
        const selectedTotp = error.mfa.selected === "totp";
        const sip_response = {
            tfa: selectedTotp ? "GA" : "Email",
            showCodeField: true,
            // CT-129: pbx-api now takes a `channel`, so the form can offer every method the identity
            // holds — legacy's "send code in email" under an authenticator challenge works again.
            showSendMail: error.mfa.channels.includes("email"),
            availabletfa: error.mfa.channels.map(legacyChannelName),
            // The challenge already delivered a code — the form must not auto-fire a resend.
            code_already_sent: !selectedTotp && error.mfa.sent,
        };
        return new LoginFailedError(selectedTotp ? "Enter Two factor verification code" : "Mail Sent with code", {
            data: { meta: { sip_response } },
        });
    }

    if (error.code === "invalid_code") {
        return new LoginFailedError("Two step verification code fail", { data: {} });
    }

    // CT-376: password (and 2FA) passed, but the password is past the account's policy. The reset
    // token opens the choose-a-new-password page, which signs them in.
    if (error.code === "password_expired" && error.resetToken) {
        return new LoginFailedError(translate("Your password has expired"), {
            data: { code: "password_expired", reset_token: error.resetToken },
        });
    }

    // CT-129: pbx-api no longer holds the parked password login — only a fresh password submit helps.
    if (error.code === "mfa_expired") {
        return new LoginFailedError("Code expired", { data: { code: "mfa_expired" } });
    }

    // CT-129: the lock reports how long it has left, so say the real wait rather than the
    // window's full length — someone four minutes in should not be told fifteen.
    const lockMinutes = error.retryAfter ? Math.max(1, Math.ceil(error.retryAfter / 60)) : 15;
    const messageByCode: Record<string, string> = {
        invalid_credentials: translate("Invalid credentials"),
        password_expired: translate("Your password has expired"),
        missing_credentials: translate("Invalid credentials"),
        no_access: translate("Forbidden"),
        // CT-96: a forced-SSO tenant refuses the password before verifying it. Tell the user to use
        // single sign-on rather than implying the password was wrong.
        sso_required: translate("This organization requires single sign-on."),
        // CT-614: only the owner of a closed account may still sign in.
        account_closed: translate("This account is closed. Ask the account owner to reactivate it."),
        // CT-371: outside the organization's Login Access lists — name the address, so the person can
        // give their administrator something to act on.
        login_location_denied: error.ip
            ? translate("You can't sign in from this location ({ip}). Please contact your administrator.", {
                  ip: error.ip,
              })
            : translate("You can't sign in from this location. Please contact your administrator."),
        // CT-501: our side is down. Say so, never blame the password.
        idp_unavailable: translate("Login is temporarily unavailable. Please try again in a few minutes."),
        rbac_unavailable: translate("Login is temporarily unavailable. Please try again in a few minutes."),
        mfa_locked:
            lockMinutes === 1
                ? translate("Sorry, you have exceeded the number of tries. Please try again in {minutes} minute.", {
                      minutes: lockMinutes,
                  })
                : translate("Sorry, you have exceeded the number of tries. Please try again in {minutes} minutes.", {
                      minutes: lockMinutes,
                  }),
        network_error: translate("A server error occurred during request execution"),
    };
    // CT-501: any other 5xx (a crash, a 502 at the edge) is also our outage, not a code to show.
    const fallback =
        error.status >= 500
            ? translate("Login is temporarily unavailable. Please try again in a few minutes.")
            : error.code;
    return new LoginFailedError(messageByCode[error.code] ?? fallback, {
        data: error.code === "mfa_locked" ? { code: "mfa_locked" } : {},
    });
}

/**
 * Login via pbx-api POST /api/auth/login (CT-36/CT-135): on success the access token is in
 * memory and the rotating __Host-pbx_rt refresh cookie is set — no token in the response
 * handling, no sip embedded code, no handoff cookie.
 */
export async function login(
    data: LoginData & { timezone: string },
    _currentLocale?: string
): Promise<{ appOrigin: string | null }> {
    try {
        setLoginLoading(true);
        const session = await loginWithCredentials({
            email: data.email,
            password: data.password ?? "",
            // The login form's OTP lives in `code`; profile-settings TFA flows use
            // `one_time_password`. Either one is the /api/auth `otp`.
            otp: data.one_time_password || data.code || undefined,
            // The form's "send code in email" link (and its Resend) set this; with no otp it makes
            // pbx-api re-deliver the challenge over email instead of the authenticator.
            channel: data.send_me_email ? "email" : undefined,
        });
        resetAllLoginState();
        return { appOrigin: session?.appOrigin ?? null };
    } catch (error) {
        throw mapAuthErrorToLoginFailed(error);
    } finally {
        setLoginLoading(false);
    }
}

export async function register(data: { email: string }) {
    try {
        setRegisterLoading(true);
        const client = getPbxClient();
        return await client.post(`${getRootApiUrl()}/register`, data);
    } finally {
        setRegisterLoading(false);
    }
}

type RegisterInviteData = {
    token: string;
    password: string;
    password_confirmation: string;
};

/**
 * CT-57 — accept an invitation.
 *
 * Routed by the TOKEN'S SHAPE, exactly like the validate query: pbx-api mints `it_` + 64 hex, so an
 * invitation created by the new admin screen accepts against pbx-api while one already sitting in
 * someone's inbox from Laravel keeps working. Invite creation moved to pbx-api in CT-57 and this
 * half did not follow, which made every new invitation unacceptable.
 *
 * The response is normalized to the legacy shape the form already consumes ({email, redirect_url?}).
 * pbx-api also reports `sip.provisioned:false` — the account works, it just has no extension — which
 * is deliberately NOT an error here: failing the join over missing telephony would be the worse
 * outcome, and the admin sees the same fact on the users list.
 */
export async function registerFromInvite(data: RegisterInviteData) {
    try {
        setRegisterLoading(true);
        if (isPbxApiInviteToken(data.token)) {
            const client = getPbxClient();
            // Password only. The form does not collect a name, and pbx-api falls back to the
            // first/last name captured on the invitation when the keys are absent — sending empty
            // strings instead would overwrite them with nothing.
            const res = await client.post(`/invitations/${encodeURIComponent(data.token)}/accept`, {
                password: data.password,
            });
            return { data: { email: res.data?.email, sip: res.data?.sip } };
        }
        const client = getPbxClient();
        const res = await client.post(`/invitation/validate/${encodeURIComponent(data.token)}`, data);
        return res.data;
    } finally {
        setRegisterLoading(false);
    }
}

export async function verifyUser(data: { user: string; hash: string }) {
    const client = getPbxClient();
    const { data: user } = await client.post(`${getRootApiUrl()}/verify/${data.user}/${data.hash}`, {});
    // No auto-login: the legacy token-URL login flow is retired (CT-135) — the caller routes the
    // user onward and they authenticate via /api/auth/login.
    return user;
}

/** CT-336 — both halves moved off Laravel onto pbx-api, which owns the token (via sip-idp), the
 *  branded email, and the session that follows a successful reset. */
export async function forgotPassword(data: { email: string }) {
    try {
        setForgotPasswordLoading(true);
        await requestPasswordReset(data.email);
    } finally {
        setForgotPasswordLoading(false);
    }
}

/** The token identifies the identity, so neither the address nor the confirmation field is sent —
 *  confirming a password is a form concern, and the address in a URL is a leak into history. */
export async function resetPassword(data: { token: string; password: string }): Promise<PasswordResetOutcome> {
    try {
        setResetPasswordLoading(true);
        return await redeemPasswordReset(data.token, data.password);
    } finally {
        setResetPasswordLoading(false);
    }
}

/**
 * Hang the softphone up and check the agent out of their queues.
 *
 * Every step is guarded on its own. Sharing one try meant a stale dialog in wp_sessions, or a
 * check-out that 401'd, took the rest down with it — most importantly wp_stop(), which is what
 * actually unregisters the device. And a throw escaping here aborted the whole of logout().
 */
async function logoutWebphone() {
    if (!window.webPhone?.config?.checkedIn) {
        console.warn("Webphone not checked in, skipping logoutWebphone");
        return;
    }

    const {
        wp_sessions = [],
        hangup,
        wp_stop,
    } = window.webPhone.phone || { wp_sessions: [], hangup: () => {}, wp_stop: () => {} };

    bridge.emit(BRIDGE_EVENTS.TOGGLE_WEBPHONE, { show: false });

    try {
        Object.values(wp_sessions).forEach((session) => hangup(session));
    } catch (error) {
        console.warn("webphone: hangup failed", error);
    }

    try {
        await getSipClient().get("/cc_agent/checkOut");
    } catch (error) {
        console.warn("webphone: agent check-out failed", error);
    }

    try {
        wp_stop();
    } catch (error) {
        console.warn("webphone: wp_stop failed", error);
    }
}

/**
 * The full logout: hang the softphone up and check the agent out, drop every credential and
 * cached surface, then reload onto the login page.
 *
 * `reason` labels an involuntary one (inactivity) so the login page — in this tab and in the
 * others the broadcast reaches — can say why.
 */
export async function logout(reason?: SessionEndReason) {
    const { isLoggingOut } = authStore.getState();
    if (isLoggingOut) {
        return;
    }

    authStore.setState((state) => {
        state.isLoggingOut = true;
    });

    try {
        await logoutWebphone();
        // Clears the __Host-pbx_rt refresh cookie server-side + the in-memory token, and tells
        // the other tabs why.
        await logoutSession(reason);

        // Extract root domain from current hostname (e.g., "tenant.pbxdev.test" -> "pbxdev.test")
        const hostParts = window.location.hostname.split(".");
        const rootDomain = hostParts.length > 2 ? hostParts.slice(-2).join(".") : window.location.hostname;
        document.cookie = `currentSession=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${rootDomain}`;

        clearSessionCookie();
        // Clear all auth store state
        clearAuthStore();

        // Clear chat/messenger state and DOM
        useInboxStore.getState().setChatInitialized(false);
        useInboxStore.getState().setStore({});
        const chatPanel = document.getElementById("chatPanelCt");
        if (chatPanel) {
            chatPanel.innerHTML = "";
        }
        const messengerContainer = document.querySelector(".messenger-chat-container");
        if (messengerContainer) {
            messengerContainer.innerHTML = "";
        }
        delete (window as any).chat;
        delete (window as any).initPbxChat;

        // Clear mobile device modal state on logout
        mobileDeviceModalStore.persist.clearStorage();
    } catch (error) {
        // Getting the user out ranks above tidying up. Without this a throw anywhere above — a
        // webphone bundle in a bad state, a network blip — skipped the redirect and every clear
        // below it, and the user who clicked Log out stayed on the dashboard.
        console.warn("[auth] logout teardown failed; signing out anyway", error);
    } finally {
        authStore.setState((state) => {
            state.isLoggingOut = false;
        });
        clearAccountStorage();
        // In the finally so it happens exactly once, on every path.
        redirectToLogin(reason);
    }
}

export async function generateQrCode(email: string, regenerate: boolean = true) {
    try {
        const client = getPbxClient();
        const { data } = await client.get("/generate-qr", {
            params: {
                email,
                regenerate,
            },
        });
        return data;
    } catch (err) {
        return null;
    }
}

export const useAuthStore = createSelectors(authStore);

/**
 * Check if user is authenticated: the in-memory Bearer is the single credential (CT-36).
 */
export function isAuthenticated(): boolean {
    return !!authStore.getState().authToken;
}

/**
 * Clear all auth store state - use this for complete cleanup on logout
 */
export function clearAuthStore() {
    authStore.setState((state) => {
        // Clear auth tokens
        state.authToken = null;
        state.sipSubdomain = null;

        // Clear login state
        state.isLoggedIn = false;
        state.isUnauthorized = false;
    });

    // Clear persisted state from localStorage
    authStore.persist.clearStorage();

    // Also reset login flow state
    resetAllLoginState();
}
